import React from "react";
import { BASE_COLUMN_TYPES, BaseColumnType } from "../../columns/config/types";
import { ReportColumnConfigBase } from "../../columns/config/ReportColumnConfig";
import { ClientColumn } from "../common";

const REFUND_STATUS_LABELS: Record<string, string> = {
  PENDING: "Refund pending",
  REFUNDED: "Refunded",
  FAILED: "Refund failed",
  VOIDED: "Voided",
};

const FORM_TYPE_LABELS: Record<string, string> = {
  INVOICE: "Invoice",
  "DEPOSIT INVOICE": "Deposit Invoice",
};

const ORepColumn: ReportColumnConfigBase<typeof BASE_COLUMN_TYPES.Avatar> = {
  header: <>Order&nbsp;Rep</>,
  type: BASE_COLUMN_TYPES.Avatar,
  fields: [
    "sales_rep_user_id",
    "sales_rep_first_name",
    "sales_rep_last_name",
    "sales_rep_avatar_file_id",
  ],
  accessorKey: "sales_rep",
  canSort: true,
  orderByField: "sales_rep",
  width: 150,
  avatarOptions: {
    withFullName: true,
  },
  dataTransform: ({
    sales_rep_first_name,
    sales_rep_last_name,
    sales_rep_avatar_file_id,
  }) => ({
    full_name: `${sales_rep_first_name} ${sales_rep_last_name}`,
    avatar_file_id: sales_rep_avatar_file_id,
  }),
};

const FormNumberColumn: ReportColumnConfigBase<typeof BASE_COLUMN_TYPES.Link> = {
  header: <>Form&nbsp;#</>,
  type: BASE_COLUMN_TYPES.Link,
  fields: ["form_number", "job_number"],
  accessorKey: "form_number",
  canSort: true,
  orderByField: "form_number",
  width: 83,
  dataTransform: ({ form_number, job_number }) => ({
    href: job_number ? `/project/${job_number}` : "",
    text: form_number ?? "\u2014",
  }),
};

const FormTypeColumn: ReportColumnConfigBase<typeof BASE_COLUMN_TYPES.Text> = {
  header: "Form type",
  type: BASE_COLUMN_TYPES.Text,
  fields: ["parent_type"],
  accessorKey: "parent_type",
  canSort: true,
  orderByField: "form_type",
  width: 90,
  dataTransform: ({ parent_type }) => ({
    text: FORM_TYPE_LABELS[parent_type] ?? parent_type,
  }),
};

const StatusColumn: ReportColumnConfigBase<typeof BASE_COLUMN_TYPES.Text> = {
  header: "Status",
  type: BASE_COLUMN_TYPES.Text,
  fields: ["refund_status"],
  accessorKey: "refund_status",
  canSort: true,
  orderByField: "status_name",
  width: 120,
  dataTransform: ({ refund_status }) => ({
    text: REFUND_STATUS_LABELS[refund_status] ?? refund_status,
  }),
};

const ProjectNameColumn: ReportColumnConfigBase<typeof BASE_COLUMN_TYPES.Link> = {
  header: "Project",
  type: BASE_COLUMN_TYPES.Link,
  fields: ["job_name", "job_number"],
  accessorKey: "job_name",
  canSort: true,
  orderByField: "job_name",
  width: 150,
  textOptions: {
    truncate: true,
  },
  dataTransform: ({ job_name, job_number }) => ({
    href: `/project/${job_number}`,
    text: job_name,
  }),
};

const RefundDateColumn: ReportColumnConfigBase<typeof BASE_COLUMN_TYPES.Date> = {
  header: "Refund date",
  type: BASE_COLUMN_TYPES.Date,
  fields: ["date_refunded"],
  accessorKey: "date_refunded",
  canSort: true,
  orderByField: "date_refunded",
  width: 100,
};

const PaymentProviderColumn: ReportColumnConfigBase<typeof BASE_COLUMN_TYPES.Text> = {
  header: "Payment provider",
  type: BASE_COLUMN_TYPES.Text,
  fields: ["payment_provider"],
  accessorKey: "payment_provider",
  canSort: true,
  orderByField: "payment_provider",
  width: 90,
};

const AmountRefundedColumn: ReportColumnConfigBase<typeof BASE_COLUMN_TYPES.Money> = {
  header: "Amount refunded",
  type: BASE_COLUMN_TYPES.Money,
  fields: ["amount"],
  accessorKey: "amount",
  canSort: true,
  orderByField: "amount",
  width: 150,
  moneyOptions: { decimalPlaces: 2 },
  dataTransform: ({ amount }) => ({
    amount,
    currency_id: "", // defaults to USD
  }),
};

export const Columns: ReportColumnConfigBase<BaseColumnType>[] = [
  ORepColumn,
  FormNumberColumn,
  FormTypeColumn,
  StatusColumn,
  ProjectNameColumn,
  ClientColumn,
  RefundDateColumn,
  PaymentProviderColumn,
  AmountRefundedColumn,
];
